import { memo, useEffect } from 'react'
import throttle from 'lodash/throttle'
import useHover from '@/utils/useHover'
import touchHandler from '@/utils/touchHandler'
import { MoreHorizontal, MoreVertical } from 'react-feather'
import classNames from 'classnames'
import { useRecoilState } from 'recoil'
import { ocrState } from '@/store'

const firstTouch = touchHandler()

const styles = (direction) => ({
  width: direction === 'horizontal' ? '8px' : '100%',
  height: direction === 'horizontal' ? '100%' : '8px',
  cursor: direction === 'horizontal' ? 'col-resize' : 'row-resize',
})

function ResizeHandle({
  direction = 'vertical',
  styleProps,
  onHandleMove,
  onStopResizing,
  onStartResizing,
  onStartHover,
  onFinishHover,
  isDragging,
  className,
  ...props
}) {
  const [ocr] = useRecoilState(ocrState)
  const [hoverRef, isHovered] = useHover()
  let size = null

  const showHover = isHovered || isDragging

  const resize = (e) => {
    if (direction === 'horizontal') {
      const newSize = e.clientX
      size = newSize
      onHandleMove(newSize)
      return
    }
    const newSize = e.clientY
    // eslint-disable-next-line no-unused-vars
    size = newSize
    onHandleMove(newSize)
  }

  useEffect(() => {
    if (isHovered || size) return onStartHover()

    onFinishHover()
  }, [isHovered, size])

  const throttleResize = throttle(resize, 20, { trailing: false })

  const stopMouseResizing = () => {
    document.removeEventListener('mousemove', throttleResize)
    document.removeEventListener('mouseup', stopMouseResizing)
    if (onStopResizing) {
      onStopResizing(size)
      size = null
    }
  }

  const startMouseResizing = () => {
    document.addEventListener('mousemove', throttleResize)
    document.addEventListener('mouseup', stopMouseResizing)
    if (onStartResizing) onStartResizing()
  }

  const stopTouchResizing = (e) => {
    const touchEvent = firstTouch.end(e)
    if (!touchEvent) return

    size = null
    document.removeEventListener('touchmove', throttleTouchResize)
    document.removeEventListener('touchend', stopTouchResizing)
    onStopResizing()
  }

  const touchResize = (e) => {
    const touchEvent = firstTouch.get(e)
    if (!touchEvent) return

    resize(touchEvent)
  }
  const throttleTouchResize = throttle(touchResize, 20, { trailing: false })

  const startTouchResizing = (e) => {
    const touchEvent = firstTouch.start(e)
    if (!touchEvent) return

    size =
      props.direction === 'horizontal' ? touchEvent.clientX : touchEvent.clientY
    document.addEventListener('touchmove', throttleTouchResize)
    document.addEventListener('touchend', stopTouchResizing)
    onStartResizing()
  }

  const handleClassName = classNames(
    className,
    'w-full center bg-gray-300 transition-colors ease-in-out duration-300',
    { 'bg-primary': showHover }
  )

  const MoreIcon =
    direction === 'vertical' ? (
      <MoreHorizontal
        size="20px"
        className="transition-colors ease-in-out duration-300 text-gray-500"
        style={{ color: showHover ? 'white' : undefined }}
      />
    ) : (
      <MoreVertical
        size="20px"
        className="transition-colors absolute ease-in-out duration-300 text-gray-500"
        style={{
          left: '-6px',
          top: 'calc(50% - 10px)',
          color: showHover ? 'white' : undefined,
        }}
      />
    )

  return (
    <div
      ref={hoverRef}
      style={{ ...styleProps, ...styles(direction) }}
      onMouseDown={startMouseResizing}
      onMouseUp={stopMouseResizing}
      onTouchStart={startTouchResizing}
      onTouchEnd={stopTouchResizing}
      onTouchCancel={stopTouchResizing}
      role="button"
      className={handleClassName}
      {...props}
    >
      {!ocr && MoreIcon}
    </div>
  )
}

export default memo(ResizeHandle)
